import { Params } from '@angular/router';
import cloneDeep from 'lodash/cloneDeep';
import { HisSearchCriteria } from '../model/search/his-search';

export class FilterUtils {

    /**
     * Converts a filter object to an array of `HisSearchCriteria`
     */
    public static toFilterCrtieria( filter: Record<string, any> ) {
        if ( !filter ) {
            return [];
        }

        return Object.entries( filter ).map( ( [field, value] ) => {
            const criteria = new HisSearchCriteria();
            criteria.field = field;
            criteria.value = value;
            return criteria;
        } );
    }

    /**
     * Converts a filter object to query parameters that can be used in the URL:
     * ```ts
     * const filter = { 'Person.id': 2, 'Person.name': 'John' };
     * const params = FilterUtils.convertFilterToParams( filter );
     * // params = {
     * //   'tableFilter.criteria0.field': 'Person.id',
     * //   'tableFilter.criteria0.value': 2,
     * //   'tableFilter.criteria1.field': 'Person.name',
     * //   'tableFilter.criteria1.value': 'John'
     * // }
     * ```
     */
    public static convertFilterToParams( filter: Record<string, any> ): Params {
        if ( !filter ) {
            return {};
        }

        // filterParam = ["tableFilter.criteria${index}.field=Person.id", "tableFilter.criteria${index}.value=2"]
        const filterParams: Record<string, string> = {};
        Object.entries( filter ).forEach( ( [field, value], index ) => {
            filterParams[`tableFilter.criteria${index}.field`] = field;
            filterParams[`tableFilter.criteria${index}.value`] = value;
        } );
        return filterParams;
    }

    /**
     * Converts query parameters that can be used in the URL to a filter object:
     * ```ts
     * const params = {
     *   'tableFilter.criteria0.field': 'Person.id',
     *   'tableFilter.criteria0.value': 2,
     *   'tableFilter.criteria1.field': 'Person.name',
     *   'tableFilter.criteria1.value': 'John'
     * };
     * const filter = FilterUtils.convertParamsToFilter( params );
     * // filter = { 'Person.id': 2, 'Person.name': 'John' }
     * ```
     * This method only considers parameters that start with `tableFilter.criteria` and
     * have the correct `<idx>.field` and `<idx>.value` structure. Other parameters are ignored.
     */
    public static convertParamsToFilter( params: Params ): Record<string, string> {
        if ( !params ) {
            return {};
        }

        const filter: Record<string, string> = {};
        FilterUtils.getFilterParams( params ).forEach( ( [key, value] ) => {
            const index = key.match( /tableFilter\.criteria(\d+)\.field/ )?.[1];
            if ( index ) {
                const field = value;
                const valueKey = `tableFilter.criteria${index}.value`;
                if ( params[valueKey] ) {
                    filter[field] = params[valueKey];
                }
            }
        } );
        return filter;
    }

    /**
     * Sets all filter-related parameters (those starting with `tableFilter.criteria`) to null explicitly.
     */
    public static nullAllFilterParams( params: Params ): Params {
        if ( !params ) {
            return {};
        }

        const newParams = cloneDeep( params );
        FilterUtils.getFilterParams( newParams ).forEach( ( [key] ) => newParams[key] = null );
        return newParams;
    }

    /**
     * Retrieves all filter-related parameters (those starting with `tableFilter.criteria`) from the given query parameters object.
     */
    public static getFilterParams( params: Params ): [string, any][] {
        if ( !params ) {
            return [];
        }

        return Object.entries( params ).filter( ( [key] ) => key.startsWith( 'tableFilter.criteria' ) );
    }
}
